import { take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { DatePipe } from '@angular/common';
import { FileUploadService } from '@shared/components/file-upload';
import { NamingConventionType, NamingConventionItemType } from '@shared/enums';
import {
  NamingConvention,
  NamingConventionItem,
  ProjectSetup,
} from '@shared/models';
import {
  DprDetails,
  NamingConventionDprDetails,
  DprAttachment,
} from '@shared/models/dpr';
import { UserDetailsFacade } from '@shared/state/facades';
import { getFixedLength, sanitizeFileName } from '@shared/utils';
import cloneDeep from 'lodash.clonedeep';

@Injectable()
export class NamingConventionService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private fileUploadService: FileUploadService,
    private datePipe: DatePipe
  ) {}

  isRenameAppendixFileEnabled(namingConvention: NamingConvention): boolean {
    if (
      !namingConvention?.renameAppendix ||
      !namingConvention?.namingConventionItemList
    ) {
      return false;
    }

    return !!namingConvention.namingConventionItemList.find(
      (x) => x.key === NamingConventionType.Appendix
    )?.value?.length;
  }

  isRenameAttachmentFileEnabled(namingConvention: NamingConvention): boolean {
    if (
      !namingConvention?.renameAttachment ||
      !namingConvention?.namingConventionItemList
    ) {
      return false;
    }

    return !!namingConvention.namingConventionItemList.find(
      (x) => x.key === NamingConventionType.Attachment
    )?.value?.length;
  }

  isRenameReportFileEnabled(namingConvention: NamingConvention): boolean {
    if (
      !namingConvention?.renameReport ||
      !namingConvention?.namingConventionItemList
    ) {
      return false;
    }

    return !!namingConvention.namingConventionItemList.find(
      (x) => x.key === NamingConventionType.Report
    )?.value?.length;
  }

  getCustomFileName(
    namingConventionType: NamingConventionType,
    namingConventionItems: NamingConventionItem[],
    details: NamingConventionDprDetails,
    placeholderName?: string
  ): string {
    const items: NamingConventionItem[] = cloneDeep(namingConventionItems);
    let fileName = (items || [])
      .sort((x, y) => x.priority - y.priority)
      .reduce(this.computeNamingConventionItem.bind(this, details), '');

    if (!fileName && placeholderName) {
      fileName = placeholderName;
    }

    fileName = sanitizeFileName(fileName);

    switch (namingConventionType) {
      case NamingConventionType.Report:
        fileName = `${fileName}.pdf`;
        break;
      case NamingConventionType.ExportExcel:
        fileName = `${fileName}.xlsx`;
        break;
      case NamingConventionType.Attachment:
        fileName = `${fileName}${details.fileExtension}`;
        break;
      case NamingConventionType.Appendix:
        fileName = `${fileName}.pdf`;
        break;
    }

    return fileName;
  }

  computeNamingConventionItem(
    details: NamingConventionDprDetails,
    fileName: string,
    namingConventionItem: NamingConventionItem
  ): string {
    let conventionResult: string;

    switch (namingConventionItem.itemType) {
      case NamingConventionItemType.FreeText:
      case NamingConventionItemType.Separator:
      case NamingConventionItemType.ProjectName:
      case NamingConventionItemType.ProjectAbbreviation:
      case NamingConventionItemType.ScopeName:
      case NamingConventionItemType.VesselLocationName:
        conventionResult = namingConventionItem.value || '';
        break;
      case NamingConventionItemType.Date:
        conventionResult =
          namingConventionItem.value != null
            ? this.datePipe.transform(
                details.dprDate,
                namingConventionItem.value
              )
            : '';
        break;
      case NamingConventionItemType.DPRNumber:
        conventionResult = getFixedLength(details.dprNumber, 4);
        break;
      case NamingConventionItemType.RevisionNumber:
        conventionResult = getFixedLength(details.dprRevision, 2);
        break;
      case NamingConventionItemType.FileNumber:
        conventionResult = getFixedLength(details.fileNumber, 2);
        break;
      default:
        conventionResult = '';
    }

    return fileName + conventionResult;
  }

  applyNamingConvention(dprDetails: DprDetails): void {
    if (!dprDetails) {
      return;
    }

    this.userDetailsFacade.query.projectSetup$
      .pipe(
        take(1),
        tap((projectSetup: ProjectSetup) => {
          const namingConvention = projectSetup.namingConvention;

          const setAttachmentDisplayNameAction = (
            dprAttachment: DprAttachment,
            namingConventionItems: NamingConventionItem[],
            namingConventionType: NamingConventionType
          ): void => {
            if (dprAttachment.attachment) {
              const extension = this.fileUploadService.getExt(
                dprAttachment.attachment.name
              );

              const details: NamingConventionDprDetails = {
                dprDate: dprDetails.dprDateTime,
                dprRevision: dprDetails.revision,
                dprNumber: dprDetails.dprDisplayId,
                fileExtension: extension ? `.${extension}` : '',
                fileNumber: dprAttachment.attachment.dprAttachmentOrder,
              };

              dprAttachment.attachment.displayName = this.getCustomFileName(
                namingConventionType,
                namingConventionItems,
                details
              );
            }
          };

          if (this.isRenameAttachmentFileEnabled(namingConvention)) {
            const attachmentNamingConventionItems =
              namingConvention.namingConventionItemList.find(
                (nc) => nc.key === NamingConventionType.Attachment
              );

            if (dprDetails.dprActivities) {
              dprDetails.dprActivities.forEach((dprActivity) => {
                if (dprActivity.dprActivityAttachments) {
                  dprActivity.dprActivityAttachments.forEach(
                    (dprAttachment) => {
                      setAttachmentDisplayNameAction(
                        dprAttachment,
                        attachmentNamingConventionItems.value,
                        NamingConventionType.Attachment
                      );
                    }
                  );
                }
              });
            }

            if (dprDetails.dprParallelActivities) {
              dprDetails.dprParallelActivities.forEach(
                (dprParallelActivity) => {
                  if (dprParallelActivity.dprParallelActivityAttachments) {
                    dprParallelActivity.dprParallelActivityAttachments.forEach(
                      (dprAttachment) => {
                        setAttachmentDisplayNameAction(
                          dprAttachment,
                          attachmentNamingConventionItems.value,
                          NamingConventionType.Attachment
                        );
                      }
                    );
                  }
                }
              );
            }

            if (
              dprDetails.dprWeather &&
              dprDetails.dprWeather.dprWeatherAttachments
            ) {
              dprDetails.dprWeather.dprWeatherAttachments.forEach(
                (dprAttachment) => {
                  setAttachmentDisplayNameAction(
                    dprAttachment,
                    attachmentNamingConventionItems.value,
                    NamingConventionType.Attachment
                  );
                }
              );
            }

            if (dprDetails.dprHSE && dprDetails.dprHSE.dprhseAttachments) {
              dprDetails.dprHSE.dprhseAttachments.forEach((dprAttachment) => {
                setAttachmentDisplayNameAction(
                  dprAttachment,
                  attachmentNamingConventionItems.value,
                  NamingConventionType.Attachment
                );
              });
            }

            if (
              dprDetails.dprPersonnel &&
              dprDetails.dprPersonnel.dprPersonnelAttachments
            ) {
              dprDetails.dprPersonnel.dprPersonnelAttachments.forEach(
                (dprAttachment) => {
                  setAttachmentDisplayNameAction(
                    dprAttachment,
                    attachmentNamingConventionItems.value,
                    NamingConventionType.Attachment
                  );
                }
              );
            }

            if (
              dprDetails.dprConsumable &&
              dprDetails.dprConsumable.dprConsumableAttachments
            ) {
              dprDetails.dprConsumable.dprConsumableAttachments.forEach(
                (dprAttachment) => {
                  setAttachmentDisplayNameAction(
                    dprAttachment,
                    attachmentNamingConventionItems.value,
                    NamingConventionType.Attachment
                  );
                }
              );
            }

            if (
              dprDetails.dprSummary &&
              dprDetails.dprSummary.dprSummaryAttachments
            ) {
              dprDetails.dprSummary.dprSummaryAttachments.forEach(
                (dprAttachment) => {
                  setAttachmentDisplayNameAction(
                    dprAttachment,
                    attachmentNamingConventionItems.value,
                    NamingConventionType.Attachment
                  );
                }
              );
            }

            if (dprDetails.dprHidden) {
              dprDetails.dprHidden.forEach((dprHidden) => {
                if (dprHidden.dprHiddenAttachments) {
                  dprHidden.dprHiddenAttachments.forEach((dprAttachment) => {
                    setAttachmentDisplayNameAction(
                      dprAttachment,
                      attachmentNamingConventionItems.value,
                      NamingConventionType.Attachment
                    );
                  });
                }
              });
            }
          }

          if (this.isRenameAppendixFileEnabled(namingConvention)) {
            const appendixNamingConventionItems =
              namingConvention.namingConventionItemList.find(
                (nc) => nc.key === NamingConventionType.Appendix
              );

            if (dprDetails.dprAppendixAttachments) {
              dprDetails.dprAppendixAttachments.forEach((dprAttachment) => {
                setAttachmentDisplayNameAction(
                  dprAttachment,
                  appendixNamingConventionItems.value,
                  NamingConventionType.Appendix
                );
              });
            }
          }
        })
      )
      .subscribe();
  }
}
